import React from "react";
import { BaseDocumentType as BaseDocumentTypeEnum } from "../../../graphql/supergraphEnums";
import { BaseDocumentType } from "../../../graphql/supergraphTypes";
import { DocumentFormValues } from "../../../hooks/documents/useDocumentProcessor";
import {
  DocumentCategoryInfo,
  DocumentData,
  DocumentSubType,
  DocumentSubTypeEnum,
} from "./types";

const TAX_FORM_US_PAGE_LINKS: Record<string, string> = {
  "IRS W-9": "https://www.irs.gov/forms-pubs/about-form-w-9",
};

const TAX_FORM_NON_US_PAGE_LINKS: Record<string, string> = {
  "IRS W-8 BEN": "https://www.irs.gov/forms-pubs/about-form-w-8-ben",
  "IRS W-8 BEN-E": "https://www.irs.gov/forms-pubs/about-form-w-8-ben-e",
  "IRS W-8 ECI": "https://www.irs.gov/forms-pubs/about-form-w-8-eci",
  "IRS W-8 EXP": "https://www.irs.gov/forms-pubs/about-form-w-8-exp",
  "IRS W-8 IMY": "https://www.irs.gov/forms-pubs/about-form-w-8-imy",
};

/**
 * Document categories. Based on the requirement documentation.
 */
const documentCategories: Partial<Record<BaseDocumentType, DocumentCategoryInfo>> = {
  [BaseDocumentTypeEnum.Id]: {
    title: "Identification document",
    description:
      "Government-issued identification containing a photograph and signature. Examples include: A passport or driver's license",
  },
  [BaseDocumentTypeEnum.AddressVerification]: {
    title: "Address Verification",
    description:
      "Utility bill, bank statement, government document, or equivalent dated within the 12 months. Examples include: Electricity bill, natural gas bill or a bank statement dated within the last 12 months",
  },
  [BaseDocumentTypeEnum.AmlRelianceLetter]: {
    title: "AML Reliance Letter",
    description:
      "Letter confirming reliance on another institution's anti-money laundering procedures. Examples include: An AML Reliance/Comfort Letter issued by a Fund Administrator/Custodian/Nominee",
  },
  [BaseDocumentTypeEnum.AnnualReport]: {
    title: "Annual Report",
    description:
      "Annual disclosure of a public company's financial and operational performance. Examples include: Annual Financial Report or Form 10-K",
  },
  [BaseDocumentTypeEnum.AuthorizedSignatoryList]: {
    title: "Authorized Signatory List",
    description:
      "Official list of individuals authorized to sign on behalf of the entity, including signature specimens. Examples include: Authorized Signatory List, Incumbency certificate, Signatory Authorization Form with signature specimens",
  },
  [BaseDocumentTypeEnum.ConfirmationOfClassBeneficiaries]: {
    title: "Confirmation of the class of beneficiaries",
    description:
      "Documentation or confirmation verifying the designated class of beneficiaries of a non-profit. Examples include: Mission Statement, Beneficiary Declaration, Trust Agreement",
  },
  [BaseDocumentTypeEnum.EvidenceOfFormation]: {
    title: "Evidence of Government Formation/Control over Entity",
    description:
      "Documentation demonstrating government oversight or approval of the entity's formation. Examples include: Government-issued Ownership Certificate, Regulatory filings showing government ownership, or screenshot of listing in a government-owned entity registry",
  },
  [BaseDocumentTypeEnum.EvidenceOfListingOnPublicExchange]: {
    title: "Evidence of listing on public exchange",
    description:
      "Evidence that entity is publicly traded and subject to requirements of a major exchange. Examples include: Screenshot of stock exchange listing, Bloomberg/Reuters report",
  },
  [BaseDocumentTypeEnum.EvidenceOfRegulation]: {
    title: "Evidence of Regulation",
    description:
      "Official documentation proving the entity is regulated by a governing authority. Examples include: Online regulatory registry search results, License verification from SEC, FCA, FINRA, or state regulators, Screenshot of online registration status",
  },
  [BaseDocumentTypeEnum.Form_990]: {
    title: "Form 990",
    description:
      "IRS form submitted by tax-exempt organizations to report financial information. Examples include: Form 990, Form 990-EZ",
  },
  [BaseDocumentTypeEnum.FormationDocument]: {
    title: "Formation Document",
    description:
      "Legal document issued by a government entity confirming the establishment and existence of an entity. Examples include: Certificate of Incorporation, Certificate of Formation",
  },
  [BaseDocumentTypeEnum.OrganizationChart]: {
    title: "Organization Chart",
    description:
      "Diagrammatic representation of an entity's governance and reporting structure. Examples include: Organizational chart depicting hierarchy and roles",
  },
  [BaseDocumentTypeEnum.OrganizationalDocument]: {
    title: "Organizational Document",
    description:
      "Formal document outlining the structure, governance, and key personnel of an entity. Examples include: Limited Partnership Agreement, Articles of Association, Operating Agreement",
  },
  [BaseDocumentTypeEnum.PayeeMismatchExplanation]: {
    title: "Payee Mismatch Explanation",
    description: "Written explanation addressing discrepancies in payee details",
  },
  [BaseDocumentTypeEnum.RegisterOfDirectors]: {
    title: "Register of directors",
    description:
      "List of company directors and their details. Examples include: Company's official director registry",
  },
  [BaseDocumentTypeEnum.RegisterOfMembers]: {
    title: "Register of members",
    description:
      "Register of members including ownership percentages. Examples include: Shareholder registry, Cap table",
  },
  [BaseDocumentTypeEnum.ShareRegister]: {
    title: "Share Register",
    description:
      "Record of issued shares and ownership details. Examples include: Stock ledger, Share issuance report",
  },
  [BaseDocumentTypeEnum.TrustDeed]: {
    title: "Trust Deed",
    description:
      "Trust Deed, Declaration of Trust, or equivalent. Examples include: Trust Agreement, Trust Deed",
  },
  [BaseDocumentTypeEnum.WSeriesTaxForm]: {
    title: "Tax document",
    description: (
      <>
        Sign this form before uploading.
        <br />
        If the subscriber is a U.S. person, use
        {Object.entries(TAX_FORM_US_PAGE_LINKS).map(([key, value], index, array) => (
          <React.Fragment key={key}>
            {" "}
            <a
              href={value}
              target="_blank"
              rel="noopener noreferrer"
              style={{ color: "inherit", textDecoration: "underline", whiteSpace: "nowrap" }}
            >
              {key}
            </a>
            {index < array.length - 1 ? "," : ""}
          </React.Fragment>
        ))}
        <br />
        If the subscriber is not a U.S. person, use the appropriate version of Form IRS W-8:
        {Object.entries(TAX_FORM_NON_US_PAGE_LINKS).map(([key, value], index, array) => (
          <React.Fragment key={key}>
            {" "}
            <a
              href={value}
              target="_blank"
              rel="noopener noreferrer"
              style={{ color: "inherit", textDecoration: "underline", whiteSpace: "nowrap" }}
            >
              {key}
            </a>
            {index < array.length - 1 ? "," : ""}
          </React.Fragment>
        ))}
      </>
    ),
  },
};

/**
 * Document sub categories
 */
export const documentSubCategories: Record<DocumentSubType, DocumentCategoryInfo> = {
  [DocumentSubTypeEnum.ArticlesOfIncorporation]: {
    title: "Articles of Incorporation",
    description:
      "Formal document outlining the structure, governance, and key personnel of an entity. Example: Charter or Articles of Incorporation",
  },
  [DocumentSubTypeEnum.CertificateOfFormation]: {
    title: "Certificate of Formation",
    description:
      "Legal document issued by a government entity confirming the establishment and existence of an entity. Example: Certificate of Formation",
  },
  [DocumentSubTypeEnum.CertificateOfIncorporation]: {
    title: "Certificate of Incorporation",
    description:
      "Legal document issued by a government entity confirming the establishment and existence of an entity. Example: Certificate of Incorporation",
  },
  [DocumentSubTypeEnum.LimitedPartnership]: {
    title: "Limited Partnership/Operating Agreement",
    description:
      "Formal document outlining the structure, governance, and key personnel of an entity. Example includes: Limited Partnership, Operating Agreement",
  },
  [DocumentSubTypeEnum.CertificateOfFormOrPartnership]: {
    title: "Certificate of Formation/Partnership",
    description:
      "Legal document issued by a government entity confirming the establishment and existence of an entity. Example: Certificate of Formation/Partnership",
  },
};

/**
 * Get the document category info
 * @param baseType - The base type of the document {@link BaseDocumentType}
 * @param subType - The sub type of the document {@link DocumentSubType}
 * @returns The document category info.
 * title -> if subType is provided, return the title from the {@link documentSubCategories} object
 * title -> if subType is not provided, return the title from the {@link documentCategories} object
 * description -> Based on the baseType, description is always taken from the {@link documentCategories} object
 */
export const getDocumentCategoryInfo = (
  baseType: string,
  subType?: string | null,
): DocumentCategoryInfo => {
  if (subType && documentSubCategories[subType as DocumentSubType]) {
    return documentSubCategories[subType as DocumentSubType];
  } else {
    return (
      documentCategories[baseType as BaseDocumentType] || {
        title: "",
        description: "",
      }
    );
  }
};

export const prepareFilesToUpload = (
  newSelectedFiles: DocumentFormValues,
): { newFiles: DocumentData[]; deleteDocIds: number[] } => {
  const newFiles: DocumentData[] = [];
  const deleteDocIds: number[] = [];

  newSelectedFiles?.documents?.forEach((document) => {
    const { id, file, uploaderInfo } = document;

    // Case 1: New file being added (no existing ID)
    if (!id && file) {
      newFiles.push(document);
      return;
    }

    // Case 2: Existing file is replaced (has ID and a new file without a URL)
    if (id && file && !file.url) {
      newFiles.push(document);
      deleteDocIds.push(id);
      return;
    }

    // Case 3: Existing file is deleted (has ID, no file, and no uploaderInfo)
    if (id && !file && !uploaderInfo) {
      deleteDocIds.push(id);
      return;
    }

    // All other cases (e.g., unchanged files, guest uploads) are ignored.
  });

  return { newFiles, deleteDocIds };
};

export const areDocumentsAvailable = (documents: DocumentData[]) => {
  return documents.every((document) => document.file != null || document.uploaderInfo != null);
};
